import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { hrApi } from '@/api';
import { EmploymentTypeSelect } from '@/components/selects';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { HrHiringStepGuide } from './HrHiringStepGuide';
import { ASSESSMENT_TYPES, INTERVIEW_ROUNDS, stageLabel } from './recruitmentUtils';
import type { HrCandidateAssessment, HrCandidateSummary, HrOnboardingTask, HrPreboardingItem } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

type Candidate360 = {
  candidate?: HrCandidateSummary & {
    stageCode: string;
    phone?: string;
    source?: string;
    screeningScore?: number;
    screeningStatus?: string;
  };
  stageHistory?: { toStage: string; fromStage?: string; notes?: string; createdAt: string }[];
  interviews?: { id: number; roundName?: string; scheduledAt: string; statusCode: string; score?: number }[];
  offers?: { id: number; offeredSalary?: number; statusCode: string; joiningDate?: string }[];
  preboardingItems?: HrPreboardingItem[];
  onboardingTasks?: HrOnboardingTask[];
  portalUrl?: string;
  assessments?: HrCandidateAssessment[];
  bgvChecks?: { id: number; verificationType?: string; statusCode: string; resultSummary?: string }[];
  probationReview?: { id: number; decisionCode?: string; statusCode: string; reviewDate?: string };
  approvalStatus?: {
    selectionStatus?: string;
    offerStatus?: string;
    offerLetterStatus?: string;
  };
};

type Props = {
  candidate: HrCandidateSummary;
  onRefresh: () => void;
  onClose: () => void;
};

export function HrCandidatePanel({ candidate, onRefresh, onClose }: Props) {
  const [data, setData] = useState<Candidate360 | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [completeInterviewOpen, setCompleteInterviewOpen] = useState(false);
  const [declineConfirmOpen, setDeclineConfirmOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [roundName, setRoundName] = useState('HR_ROUND');
  const [salary, setSalary] = useState('50000');
  const [joiningDate, setJoiningDate] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [offerContent, setOfferContent] = useState('Welcome to the team.');
  const [interviewScore, setInterviewScore] = useState('4');
  const [interviewFeedback, setInterviewFeedback] = useState('Good candidate');
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentType, setAssessmentType] = useState('TECHNICAL');
  const [assessmentName, setAssessmentName] = useState('Technical assessment');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await hrApi.candidate360(candidate.id)) as Candidate360);
    } catch (err) {
      showApiError(err, 'Failed to load candidate');
    } finally {
      setLoading(false);
    }
  }, [candidate.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const stage = data?.candidate?.stageCode ?? candidate.stageCode;
  const approval = data?.approvalStatus;

  const runHire = async () => {
    setActing(true);
    try {
      const result = await hrApi.completeHire(candidate.id);
      if (result.alreadyHired) {
        notify.info(`Already hired as employee #${String(result.employeeId)}`);
      } else {
        notify.success(`Employee #${String(result.employeeId)} created`);
      }
      onRefresh();
      onClose();
    } catch (err) {
      showApiError(err, 'Hire failed — complete offer, preboarding, and onboarding first');
    } finally {
      setActing(false);
    }
  };

  const scheduleInterview = async () => {
    if (!scheduledAt) {
      notify.error('Interview date and time are required');
      return;
    }
    setActing(true);
    try {
      await hrApi.scheduleInterview(candidate.id, {
        scheduledAt: new Date(scheduledAt).toISOString(),
        roundName,
        interviewType: roundName,
        durationMinutes: 60,
      });
      notify.success('Interview scheduled');
      setInterviewOpen(false);
      await load();
      onRefresh();
    } catch (err) {
      showApiError(err, 'Failed to schedule interview');
    } finally {
      setActing(false);
    }
  };

  const completeInterview = async () => {
    const interview = data?.interviews?.find((i) => i.statusCode === 'SCHEDULED');
    if (!interview) return;
    setActing(true);
    try {
      await hrApi.completeInterview(interview.id, {
        score: Number(interviewScore) || 4,
        feedback: interviewFeedback.trim() || 'Interview completed',
        recommendation: 'RECOMMEND',
        decisionCode: 'SELECT',
      });
      notify.success('Interview completed — candidate selected');
      setCompleteInterviewOpen(false);
      await load();
      onRefresh();
    } catch (err) {
      showApiError(err, 'Failed to complete interview');
    } finally {
      setActing(false);
    }
  };

  const acceptOffer = async () => {
    const offer = data?.offers?.find((o) => o.statusCode === 'SENT');
    if (!offer) return;
    setActing(true);
    try {
      await hrApi.acceptOffer(offer.id);
      notify.success('Offer accepted — preboarding started');
      await load();
      onRefresh();
    } catch (err) {
      showApiError(err, 'Failed to accept offer');
    } finally {
      setActing(false);
    }
  };

  const declineOffer = async () => {
    const offer = data?.offers?.find((o) => o.statusCode === 'SENT');
    if (!offer) return;
    setActing(true);
    try {
      await hrApi.declineOffer(offer.id);
      notify.success('Offer declined');
      setDeclineConfirmOpen(false);
      await load();
      onRefresh();
    } catch (err) {
      showApiError(err, 'Failed to decline offer');
    } finally {
      setActing(false);
    }
  };

  const completePreboarding = async () => {
    setActing(true);
    try {
      await hrApi.completeAllPreboarding(candidate.id);
      notify.success('Preboarding complete — moved to onboarding');
      await load();
      onRefresh();
    } catch (err) {
      showApiError(err, 'Preboarding failed');
    } finally {
      setActing(false);
    }
  };

  const completeOnboarding = async () => {
    setActing(true);
    try {
      await hrApi.completeAllOnboarding(candidate.id);
      await hrApi.approveOnboarding(candidate.id);
      notify.success('Onboarding approved — ready to hire');
      await load();
      onRefresh();
    } catch (err) {
      showApiError(err, 'Onboarding failed');
    } finally {
      setActing(false);
    }
  };

  const portalFullUrl = data?.portalUrl ? `${window.location.origin}${data.portalUrl}` : null;

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-start justify-between gap-3 p-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{candidate.fullName}</h3>
          <p className="text-sm text-muted">
            {candidate.email ?? '—'}
            {candidate.phone ? ` · ${candidate.phone}` : ''}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="info">{stageLabel(stage)}</Badge>
            {data?.candidate?.screeningScore != null && (
              <Badge variant="default">Screening: {data.candidate.screeningScore}%</Badge>
            )}
            {data?.candidate?.screeningStatus && (
              <Badge variant="default">{data.candidate.screeningStatus.replace(/_/g, ' ')}</Badge>
            )}
          </div>
        </div>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>

      {loading && <p className="text-sm text-muted">Loading candidate…</p>}

      <HrHiringStepGuide stage={stage} approvalStatus={approval} />

      <div className="flex flex-wrap gap-2">
        {stage === 'SELECTED' && approval?.selectionStatus === 'PENDING' && (
          <Button
            loading={acting}
            onClick={async () => {
              setActing(true);
              try {
                await hrApi.approveCandidateSelection(candidate.id);
                notify.success('Selection approved — you can create the offer');
                await load();
                onRefresh();
              } catch (err) {
                showApiError(err, 'Failed to approve selection');
              } finally {
                setActing(false);
              }
            }}
          >
            Approve selection
          </Button>
        )}
        {stage === 'OFFER_SENT' && approval?.offerStatus === 'PENDING' && (
          <Button
            loading={acting}
            onClick={async () => {
              setActing(true);
              try {
                await hrApi.approveCandidateOffer(candidate.id);
                notify.success('Offer approved — ready to accept');
                await load();
                onRefresh();
              } catch (err) {
                showApiError(err, 'Failed to approve offer');
              } finally {
                setActing(false);
              }
            }}
          >
            Approve offer
          </Button>
        )}
        {['APPLIED', 'SCREENING'].includes(stage) && (
          <Button
            loading={acting}
            variant="secondary"
            onClick={async () => {
              setActing(true);
              try {
                await hrApi.runAutoScreening(candidate.id);
                notify.success('Auto screening completed');
                await load();
                onRefresh();
              } catch (err) {
                showApiError(err, 'Screening failed');
              } finally {
                setActing(false);
              }
            }}
          >
            Run auto screening
          </Button>
        )}
        {['SCREENING', 'INTERVIEW_SCHEDULED', 'APPLIED', 'INTERVIEWED'].includes(stage) && (
          <Button variant="secondary" onClick={() => setAssessmentOpen(true)}>
            Schedule assessment
          </Button>
        )}
        {['SCREENING', 'INTERVIEW_SCHEDULED', 'APPLIED'].includes(stage) && (
          <Button onClick={() => setInterviewOpen(true)}>Schedule interview</Button>
        )}
        {stage === 'INTERVIEW_SCHEDULED' && data?.interviews?.some((i) => i.statusCode === 'SCHEDULED') && (
          <Button loading={acting} onClick={() => setCompleteInterviewOpen(true)}>
            Complete interview
          </Button>
        )}
        {['SELECTED', 'INTERVIEWED'].includes(stage) && (
          <Button onClick={() => setOfferOpen(true)}>Create offer</Button>
        )}
        {stage === 'OFFER_SENT' && data?.offers?.some((o) => o.statusCode === 'SENT') && (
          <>
            <Button loading={acting} onClick={() => void acceptOffer()}>
              Accept offer (HR)
            </Button>
            <Button variant="secondary" loading={acting} onClick={() => setDeclineConfirmOpen(true)}>
              Decline offer
            </Button>
          </>
        )}
        {stage === 'HIRED' && data?.probationReview?.statusCode === 'PENDING' && (
          <>
            <Button
              loading={acting}
              onClick={async () => {
                setActing(true);
                try {
                  await hrApi.submitProbationReview(data.probationReview!.id, {
                    managerRating: 4,
                    hrRating: 4,
                    managerComments: 'Meets expectations',
                  });
                  await hrApi.confirmProbation(data.probationReview!.id, 'CONFIRMED');
                  notify.success('Probation confirmed');
                  await load();
                  onRefresh();
                } catch (err) {
                  showApiError(err, 'Probation confirmation failed');
                } finally {
                  setActing(false);
                }
              }}
            >
              Confirm probation
            </Button>
            <Button
              variant="secondary"
              loading={acting}
              onClick={async () => {
                setActing(true);
                try {
                  await hrApi.confirmProbation(data.probationReview!.id, 'EXTENDED');
                  notify.success('Probation extended');
                  await load();
                } catch (err) {
                  showApiError(err, 'Failed to extend probation');
                } finally {
                  setActing(false);
                }
              }}
            >
              Extend probation
            </Button>
          </>
        )}
        {stage === 'HIRED' && data?.probationReview?.statusCode !== 'PENDING' && (
          <span className="self-center text-sm text-emerald-700 dark:text-emerald-400">Hired — employee created</span>
        )}
        {['OFFER_ACCEPTED', 'PREBOARDING'].includes(stage) && (
          <Button loading={acting} onClick={() => void completePreboarding()}>
            Complete preboarding
          </Button>
        )}
        {stage === 'ONBOARDING' && (
          <>
            <Button loading={acting} onClick={() => void completeOnboarding()}>
              Complete & approve onboarding
            </Button>
            <Button loading={acting} onClick={() => void runHire()}>
              Create employee
            </Button>
          </>
        )}
      </div>

      {portalFullUrl && ['OFFER_SENT', 'OFFER_ACCEPTED', 'PREBOARDING', 'ONBOARDING'].includes(stage) && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm dark:border-primary-800 dark:bg-primary-950/40">
          <div className="font-medium text-slate-900 dark:text-slate-100">Candidate portal</div>
          <a href={portalFullUrl} target="_blank" rel="noreferrer" className="break-all text-primary-600 underline">
            {portalFullUrl}
          </a>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {data?.preboardingItems && data.preboardingItems.length > 0 && (
          <SectionCard title={`Preboarding (${data.preboardingItems.filter((i) => i.statusCode === 'COMPLETED').length}/${data.preboardingItems.length})`}>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-slate-600 dark:text-slate-300">
              {data.preboardingItems.map((i) => (
                <li key={i.id}>
                  {i.itemLabel} — {i.statusCode}
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {data?.onboardingTasks && data.onboardingTasks.length > 0 && (
          <SectionCard title={`Onboarding (${data.onboardingTasks.filter((t) => t.statusCode === 'COMPLETED').length}/${data.onboardingTasks.length})`}>
            <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
              {data.onboardingTasks.map((t) => (
                <li key={t.id}>
                  {t.taskLabel} — {t.statusCode}
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {data?.assessments && data.assessments.length > 0 && (
          <SectionCard title="Assessments">
            <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
              {data.assessments.map((a) => (
                <li key={a.id}>
                  {a.assessmentName} ({a.assessmentType}) — {a.statusCode}
                  {a.score != null ? ` · ${a.score}` : ''}
                  {a.passed != null ? (a.passed ? ' · Pass' : ' · Fail') : ''}
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {data?.bgvChecks && data.bgvChecks.length > 0 && (
          <SectionCard title="Background verification">
            <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
              {data.bgvChecks.map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-2">
                  <span>
                    {b.verificationType ?? 'Check'} — {b.statusCode}
                  </span>
                  {b.statusCode === 'IN_PROGRESS' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={acting}
                      onClick={async () => {
                        setActing(true);
                        try {
                          await hrApi.completeBgv(b.id, 'COMPLETED', 'Verified');
                          notify.success('BGV completed');
                          await load();
                        } catch (err) {
                          showApiError(err, 'BGV update failed');
                        } finally {
                          setActing(false);
                        }
                      }}
                    >
                      Complete
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {data?.probationReview && (
          <SectionCard title="Probation review">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Status: {data.probationReview.statusCode}
              {data.probationReview.decisionCode ? ` · ${data.probationReview.decisionCode}` : ''}
            </p>
          </SectionCard>
        )}

        {data?.stageHistory && data.stageHistory.length > 0 && (
          <SectionCard title="Stage history" className="lg:col-span-2">
            <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
              {data.stageHistory.slice(0, 8).map((h, idx) => (
                <li key={idx}>
                  {h.fromStage ? stageLabel(h.fromStage) : '—'} → {stageLabel(h.toStage)}
                  {h.notes ? ` (${h.notes})` : ''}
                </li>
              ))}
            </ul>
          </SectionCard>
        )}
      </div>

      <Modal open={interviewOpen} onClose={() => setInterviewOpen(false)} title="Schedule interview">
        <FormGrid columns={2} className="gap-3">
          <Select label="Round" value={roundName} onChange={setRoundName} options={INTERVIEW_ROUNDS} />
          <DateTimePicker label="Date & time" value={scheduledAt} onChange={setScheduledAt} required />
        </FormGrid>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setInterviewOpen(false)}>
            Cancel
          </Button>
          <Button loading={acting} onClick={() => void scheduleInterview()}>
            Schedule
          </Button>
        </div>
      </Modal>

      <Modal open={completeInterviewOpen} onClose={() => setCompleteInterviewOpen(false)} title="Complete interview">
        <FormGrid columns={2} className="gap-3">
          <Select
            label="Score (1–5)"
            value={interviewScore}
            onChange={setInterviewScore}
            options={['1', '2', '3', '4', '5'].map((v) => ({ value: v, label: v }))}
          />
          <Input
            label="Feedback"
            value={interviewFeedback}
            onChange={(e) => setInterviewFeedback(e.target.value)}
            className="col-span-2"
          />
        </FormGrid>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setCompleteInterviewOpen(false)}>
            Cancel
          </Button>
          <Button loading={acting} onClick={() => void completeInterview()}>
            Mark selected
          </Button>
        </div>
      </Modal>

      <Modal open={offerOpen} onClose={() => setOfferOpen(false)} title="Create offer letter">
        <FormGrid columns={2} className="gap-3">
          <Input label="Salary" type="number" min={0} value={salary} onChange={(e) => setSalary(e.target.value)} />
          <DatePicker label="Joining date" value={joiningDate} onChange={setJoiningDate} />
          <EmploymentTypeSelect value={employmentType} onChange={setEmploymentType} required />
          <Input
            label="Offer message"
            value={offerContent}
            onChange={(e) => setOfferContent(e.target.value)}
            className="col-span-2"
          />
        </FormGrid>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOfferOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={acting}
            onClick={async () => {
              if (!employmentType) {
                notify.error('Employment type is required');
                return;
              }
              setActing(true);
              try {
                await hrApi.createOffer(candidate.id, {
                  salary: Number(salary),
                  currency: 'USD',
                  joiningDate: joiningDate || undefined,
                  employmentType,
                  content: offerContent.trim() || 'Welcome to the team.',
                });
                notify.success('Offer created — pending approval before acceptance');
                setOfferOpen(false);
                await load();
                onRefresh();
              } catch (err) {
                showApiError(err, 'Failed to create offer');
              } finally {
                setActing(false);
              }
            }}
          >
            Create & send offer
          </Button>
        </div>
      </Modal>

      <Modal open={assessmentOpen} onClose={() => setAssessmentOpen(false)} title="Schedule assessment">
        <FormGrid columns={2} className="gap-3">
          <Select label="Type" value={assessmentType} onChange={setAssessmentType} options={ASSESSMENT_TYPES} />
          <Input label="Name" value={assessmentName} onChange={(e) => setAssessmentName(e.target.value)} />
        </FormGrid>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setAssessmentOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={acting}
            onClick={async () => {
              setActing(true);
              try {
                const scheduled = await hrApi.scheduleAssessment(candidate.id, {
                  assessmentType,
                  assessmentName: assessmentName.trim() || 'Assessment',
                });
                await hrApi.completeAssessment(scheduled.id, {
                  score: 75,
                  feedback: 'Assessment completed',
                  passed: true,
                });
                notify.success('Assessment recorded');
                setAssessmentOpen(false);
                await load();
                onRefresh();
              } catch (err) {
                showApiError(err, 'Assessment failed');
              } finally {
                setActing(false);
              }
            }}
          >
            Schedule & complete
          </Button>
        </div>
      </Modal>

      <Modal open={declineConfirmOpen} onClose={() => setDeclineConfirmOpen(false)} title="Decline offer?">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          This will mark the offer as declined and move the candidate out of the active offer flow.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeclineConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="secondary" loading={acting} onClick={() => void declineOffer()}>
            Decline offer
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function SectionCard({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('card p-4', className)}>
      <h4 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h4>
      {children}
    </div>
  );
}
